import { getErrorMessage } from '@xver/utils/error';
import { Loader, Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { CreateDomainMappingResponse } from '@xver/apis/v1/kufer/domain-mappings/model';
import { Button, InputField } from '@xver/components/Atoms';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOMAIN_MAPPINGS_QUERY_KEY } from '../hooks/useDomainMappingsQuery';
import DomainRecordsSections from './DomainRecordsSections';

interface AddDomainModalProps {
    containerId: string;
    opened: boolean;
    onClose: () => void;
}

export default function AddDomainModal({ containerId, opened, onClose }: AddDomainModalProps) {
    const { t } = useTranslation('containerEngineInstance');
    const queryClient = useQueryClient();
    const [domainName, setDomainName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [created, setCreated] = useState<CreateDomainMappingResponse | null>(null);

    useEffect(() => {
        if (opened) {
            setDomainName('');
            setCreated(null);
        }
    }, [opened]);

    const handleClose = () => {
        setDomainName('');
        setCreated(null);
        onClose();
    };

    const handleSubmit = async (e?: FormEvent) => {
        if (e) e.preventDefault();
        const trimmed = domainName.trim();
        if (!trimmed || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const response = await Endpoints.v1.Kufer.DomainMappings.createDomainMapping(
                containerId,
                { domain_name: trimmed }
            );
            await queryClient.invalidateQueries({
                queryKey: [DOMAIN_MAPPINGS_QUERY_KEY, containerId],
            });
            setCreated(response);
        } catch (err) {
            notifications.show({
                title: t('domains.createFailedTitle', 'Failed to add domain'),
                message: getErrorMessage(err),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={isSubmitting ? () => undefined : handleClose}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            {created ? (
                <div className="flex flex-col justify-start items-start gap-4">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <div className="flex items-center gap-2">
                            <IconCircleCheck size={18} color="#22c55e" />
                            <Modal.Title>
                                <span className="text-base font-bold">
                                    {t('domains.createdRecordTitle', 'Add these DNS records')}
                                </span>
                            </Modal.Title>
                        </div>
                        <p className="text-sm text-fg/68">
                            {t(
                                'domains.createdRecordHint',
                                "Add the records below at your DNS provider. Names are relative to your domain's zone — don't use @."
                            )}
                        </p>
                    </div>
                    <DomainRecordsSections
                        domainName={created.domain_name}
                        txtRecordName={created.txt_record_name}
                        txtRecord={created.txt_record}
                        dnsTarget={created.dns_target}
                        dnsRecordType={created.dns_record_type}
                    />
                    <Button width="100%" onClick={handleClose}>
                        {t('domains.done', 'Done')}
                    </Button>
                </div>
            ) : (
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col justify-start items-start gap-4"
                >
                    <div className="flex flex-col justify-start items-start">
                        <Modal.Title>
                            <span className="text-base font-bold">
                                {t('domains.addTitle', 'Add custom domain')}
                            </span>
                        </Modal.Title>
                        <p className="text-sm text-fg/68">
                            {t(
                                'domains.addDescription',
                                'Map a custom domain to this container. You will need to add a TXT record to your DNS to verify ownership.'
                            )}
                        </p>
                    </div>
                    <div className="w-full flex flex-col justify-start items-start gap-6">
                        <InputField
                            size="md"
                            label={t('domains.fieldLabel', 'Domain name')}
                            placeholder={t('domains.fieldPlaceholder', 'app.example.com')}
                            value={domainName}
                            onChange={(e) => setDomainName(e.target.value)}
                            disabled={isSubmitting}
                            autoFocus
                        />
                        <div className="w-full flex flex-col justify-start items-start gap-2.5">
                            <Button
                                width="100%"
                                type="submit"
                                disabled={!domainName.trim() || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center gap-2">
                                        <Loader size={16} color="white" />
                                        <span>{t('domains.adding', 'Adding…')}</span>
                                    </div>
                                ) : (
                                    t('domains.add', 'Add domain')
                                )}
                            </Button>
                            <Button
                                width="100%"
                                variant="secondary"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                {t('domains.cancel', 'Cancel')}
                            </Button>
                        </div>
                    </div>
                </form>
            )}
        </Modal>
    );
}
